import React, { useEffect, useState } from 'react';

// material-ui
import { Alert, Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Stack, TextField, Typography } from '@mui/material';

import { sendJson } from '../knowledgeFetch';
import { FolderAccess, PrincipalOption } from '../dataObjects/KnowledgeTypes';

export interface AccessTarget {
    kind: 'folder' | 'note';
    path: string;
    /** For a note: who can read it now. */
    current?: string[];
}

interface Props {
    /** What to change; null when the dialog is closed. */
    target: AccessTarget | null;
    onClose: () => void;
    onSaved: () => void;
}

/** Who a list of principals is, in words, using the picker's names for departments and people. */
export const describeAccess = (access: string[], options: PrincipalOption[]): string => access.map(a => {
    const known = options.find(o => o.principal === a);
    if (known) return known.name;
    if (a.startsWith('dept:')) return 'Dept: ' + a.substring(5);
    if (a.startsWith('user:')) return 'One person';
    return a;
}).join(', ');

let principalCache: PrincipalOption[] | undefined;

/**
 * IT's control for who can read a folder (its _access.yml) or one note (its access: frontmatter). IT is always
 * included: the Hub adds it, so the box is ticked and fixed. A note can also go back to its folder's rule.
 */
const AccessDialog = (props: Props): JSX.Element => {
    const [options, setOptions] = useState<PrincipalOption[]>(principalCache ?? []);
    const [folderAccess, setFolderAccess] = useState<FolderAccess>();
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const target = props.target;
        setError(undefined);
        setFolderAccess(undefined);
        if (!target) {
            return;
        }
        setIsLoading(true);
        const principals = principalCache
            ? Promise.resolve(principalCache)
            : fetch('/Knowledge/Principals', { headers: { Accept: 'application/json' } })
                .then(r => r.ok ? r.json() : Promise.reject(new Error('The list of staff could not be loaded.')))
                .then((data: { principals: PrincipalOption[] }) => (principalCache = data.principals));
        const current = target.kind === 'folder'
            ? fetch('/Knowledge/FolderAccess?folder=' + encodeURIComponent(target.path), { headers: { Accept: 'application/json' } })
                .then(r => r.ok ? r.json() : Promise.reject(new Error('The folder\'s rule could not be loaded.')))
                .then((data: FolderAccess) => {
                    setFolderAccess(data);
                    return data.own ?? data.effective.principals;
                })
            : Promise.resolve(target.current ?? []);
        Promise.all([principals, current])
            .then(([list, access]) => {
                setOptions(list);
                setSelected(new Set([...access, 'it']));
            })
            .catch((e: Error) => setError(e.message))
            .finally(() => setIsLoading(false));
    }, [props.target]);

    const toggle = (principal: string, on: boolean) => {
        setSelected(previous => {
            const next = new Set(previous);
            if (on) {
                next.add(principal);
            }
            else {
                next.delete(principal);
            }
            return next;
        });
    };

    const save = async (access: string[] | null) => {
        const target = props.target;
        if (!target) {
            return;
        }
        setIsSaving(true);
        setError(undefined);
        try {
            const reply = target.kind === 'folder'
                ? await sendJson<object>('/Knowledge/SetFolderAccess', 'PUT', { folder: target.path, access, baseVersion: folderAccess?.version ?? null })
                : await sendJson<object>('/Knowledge/NoteAccess', 'POST', { path: target.path, access });
            if (reply.status === 200 || reply.status === 201) {
                props.onSaved();
                return;
            }
            setError(reply.data.error ?? `The change could not be saved (${reply.status}).`);
        }
        catch {
            setError('The change could not be saved. Try again in a minute.');
        }
        finally {
            setIsSaving(false);
        }
    };

    const groups = options.filter(o => o.kind === 'group');
    const departments = options.filter(o => o.kind === 'dept');
    const people = options.filter(o => o.kind === 'user');
    const name = props.target ? props.target.path.replace(/\.md$/i, '') : '';

    return (
        <Dialog open={props.target !== null} onClose={props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Who can see this {props.target?.kind}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {name}{props.target?.kind === 'folder' ? '/ and everything under it, unless a note sets its own' : ''}
                </Typography>
                {folderAccess &&
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                        {folderAccess.own
                            ? 'This folder has its own rule.'
                            : `No rule of its own yet; it gets ${describeAccess(folderAccess.effective.principals, options)} from ${folderAccess.effective.source}.`}
                    </Typography>
                }
                {isLoading
                    ? <CircularProgress size={24} />
                    : <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormGroup row>
                            {groups.map(o =>
                                <FormControlLabel key={o.principal} label={o.principal === 'it' ? 'IT (always)' : o.name}
                                    control={<Checkbox size="small" checked={selected.has(o.principal)} disabled={o.principal === 'it'}
                                        onChange={(e) => toggle(o.principal, e.target.checked)} />} />
                            )}
                        </FormGroup>
                        {departments.length > 0 &&
                            <div>
                                <Typography variant="subtitle2">Departments</Typography>
                                <FormGroup row>
                                    {departments.map(o =>
                                        <FormControlLabel key={o.principal} label={o.name}
                                            control={<Checkbox size="small" checked={selected.has(o.principal)} onChange={(e) => toggle(o.principal, e.target.checked)} />} />
                                    )}
                                </FormGroup>
                            </div>
                        }
                        {people.length > 0 &&
                            <Autocomplete
                                multiple
                                size="small"
                                options={people}
                                getOptionLabel={(o) => o.name}
                                value={people.filter(o => selected.has(o.principal))}
                                onChange={(_, value) => setSelected(previous => new Set([
                                    ...Array.from(previous).filter(p => !p.startsWith('user:')),
                                    ...value.map(o => o.principal)
                                ]))}
                                renderInput={(params) => <TextField {...params} label="People" placeholder="Add a person" />}
                            />
                        }
                    </Stack>
                }
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                {props.target?.kind === 'note' &&
                    <Button disabled={isSaving || isLoading} onClick={() => save(null)} sx={{ mr: 'auto' }}>Use the folder's rule</Button>
                }
                <Button onClick={props.onClose}>Cancel</Button>
                <Button variant="contained" disabled={isSaving || isLoading} onClick={() => save(Array.from(selected))}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AccessDialog;
